import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef } from "react";

// Material UI
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";

// Assets
import lockIcon from "../../assets/icons/lock.svg";
import captureIcon from "../../assets/icons/capture-ico.svg";

// Components
import AccreditationSectionInfo from "./AccreditationSectionInfo";
import HighlightColor from "../../components/HighlightColor/index";
import Text, { types } from "../../components/Text/Text";

// Utils
import url, { getRoute } from "../../config/urls";
import { useIsInViewportStatic } from "../../hooks/useIsInViewport";
import { getCertificates } from "../../state/Certificates/actions";

const useStyles = makeStyles(theme => ({
  accreditationRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-evenly",
    marginTop: theme.spacing(10),
    padding: theme.spacing(16, 0, 0),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 530,
      marginTop: "0",
      padding: "80px 0 46px"
    }
  },
  miniTitle: {
    textAlign: "center",
    [theme.breakpoints.only("md")]: {
      fontSize: 56,
      width: 1004
    }
  },
  subtitle: {
    color: "#454F63",
    textAlign: "center",
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
      maxWidth: 530,
      width: "100%",
      margin: "0"
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: 17,
      maxWidth: "340px"
    }
  },
  accreditations: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      justifyContent: "center"
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
      gap: 0
    }
  }
}));

const useZoomDelayStyles = makeStyles({
  zoomDelay: {
    transitionDelay: props => `${props.delay}ms`
  }
});

const icons = {
  bs10008: captureIcon,
  iso27001: lockIcon
};

const AccreditationZoomItem = ({ item, delay, isVisible, isMobile }) => {
  const zoomDelayClasses = useZoomDelayStyles({ delay });

  return (
    <Zoom timeout={500} in={isVisible}>
      <AccreditationSectionInfo
        className={zoomDelayClasses.zoomDelay}
        icon={icons[item.id]}
        title="Our certification for"
        certificate={item.title}
        description={item.lead}
        badgeIcon={item.imageURL}
        badgeLink={`${getRoute(url.certifications)}#pageStart`}
        file={item.pdfURL}
        isMobile={isMobile}
      />
    </Zoom>
  );
};

const AccreditationSection = ({ className, ...rest }) => {
  const { certificationsList } = useSelector(s => s.certificates);
  const classes = useStyles();
  const ref = useRef();
  const isInViewport = useIsInViewportStatic(ref, { threshold: 0.5 });
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const dispatch = useDispatch();

  // Language TODO when localazy is appplied

  useEffect(() => {
    dispatch(getCertificates());
  }, []);

  return (
    <div className={classes.accreditationRoot} {...rest}>
      <Text
        type={types.miniSectionTitle}
        className={classes.miniTitle}
        component="h2"
      >
        <HighlightColor>{`Kulpa's`}</HighlightColor> Technology Is Certified
      </Text>
      <Text
        type={types.titleDescription}
        className={classes.subtitle}
        component="p"
      >
        Ensuring that your files are safe, secure and legally admissible
      </Text>
      <div className={classes.accreditations} ref={ref}>
        {certificationsList &&
          certificationsList.map((item, index) => (
            <AccreditationZoomItem
              key={item.id}
              item={item}
              delay={index * 500}
              isVisible={isMobile || isInViewport}
              isMobile={isMobile}
            />
          ))}
      </div>
    </div>
  );
};

export default AccreditationSection;
